import { useLayoutEffect, type ReactNode } from 'react'

import { EvieGroundLayer } from 'src/components/Evie/EvieGroundLayer'
import { UiProvider } from 'src/design-system/ui'
import { evieTokens } from 'src/design-system/ui/themes/evie'
import './evie-shell.css'

interface EvieUiShellProps {
  children: ReactNode
}

/** Document paint reads tokens directly: provider variables inherit downward,
 * so the root/body cannot read them. Restore the host paint on unmount. */
const useEvieDocumentCanvas = () => {
  useLayoutEffect(() => {
    const root = document.documentElement
    const { body } = document
    const previousColorScheme = root.style.colorScheme
    root.style.colorScheme = 'light'
    const previousRootBackground = root.style.backgroundColor
    const previousBodyBackground = body.style.backgroundColor
    const previousBodyColor = body.style.color

    body.classList.add('evie-document')
    root.style.backgroundColor = evieTokens.color.background
    body.style.backgroundColor = evieTokens.color.background
    body.style.color = evieTokens.color.foreground

    return () => {
      root.style.colorScheme = previousColorScheme
      body.classList.remove('evie-document')
      root.style.backgroundColor = previousRootBackground
      body.style.backgroundColor = previousBodyBackground
      body.style.color = previousBodyColor
    }
  }, [])
}

/** Existing Evie route shell owns document paint, page height and ground.
 * UiProvider supplies the same scoped theme used by embedded Evie. Shared
 * isolation keeps the negative-z ground above the canvas and below content. */
const EvieUiShell = ({ children }: EvieUiShellProps) => {
  useEvieDocumentCanvas()

  return (
    <UiProvider theme="evie" mode="light" className="evie-shell">
      <EvieGroundLayer />
      {children}
    </UiProvider>
  )
}

export default EvieUiShell
